import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { RefreshTokenResponse } from '../types/authModel';

const BASE_URL = 'https://adroj-api.atplindia.org/api';

// const BASE_URL = 'https://api.adroj.com/api';

const TOKEN_KEY = 'adroj_access_token';
const REFRESH_TOKEN_KEY = 'adroj_refresh_token';
const USER_KEY = 'adroj_user';

// ---------------------------------------------------------------------------
// Local storage helpers — shared by httpClient (interceptors) and authService.
// ---------------------------------------------------------------------------
export const getAccessToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);

export const getStoredUser = <T = unknown>(): T | null => {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as T) : null;
};

export const setAuthTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const setStoredUser = (user: unknown): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearAuthTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const httpClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Endpoints that have no access token yet — never attach a stale Authorization header.
const NO_AUTH_HEADER_ENDPOINTS = ['/auth/admin-login', '/auth/refresh-token'];

// Endpoints that should never trigger the silent-refresh-and-retry flow on a 401
// (refreshing a refresh-token call, or retrying a login/logout, makes no sense).
const NO_REFRESH_RETRY_ENDPOINTS = ['/auth/admin-login', '/auth/refresh-token', '/auth/logout'];

const matchesEndpoint = (url: string | undefined, list: string[]) => !!url && list.some((p) => url.includes(p));

// ---------------------------------------------------------------------------
// Request interceptor — attach bearer token to every request except auth ones.
// ---------------------------------------------------------------------------
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && !matchesEndpoint(config.url, NO_AUTH_HEADER_ENDPOINTS)) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor — on a 401, silently refresh the access token using
// the refresh token and retry the original request. Concurrent 401s while a
// refresh is already in flight are queued and replayed once it resolves.
// ---------------------------------------------------------------------------
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

const redirectToLogin = () => {
  clearAuthTokens();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (!originalRequest || matchesEndpoint(originalRequest.url, NO_REFRESH_RETRY_ENDPOINTS)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Another request already triggered a refresh — wait for it, then retry.
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (token: string) => {
              originalRequest.headers = originalRequest.headers ?? {};
              (originalRequest.headers as any).Authorization = `Bearer ${token}`;
              resolve(httpClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post<RefreshTokenResponse>(`${BASE_URL}/auth/refresh-token`, {
          refreshToken,
        });

        if (!data.success || !data.result) {
          throw new Error(data.message || 'Invalid or expired refresh token.');
        }

        const { token: newToken, refreshToken: newRefreshToken } = data.result;
        setAuthTokens(newToken, newRefreshToken);
        processQueue(null, newToken);

        originalRequest.headers = originalRequest.headers ?? {};
        (originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default httpClient;